import informationCircle from '@iconify/icons-heroicons-solid/information-circle';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Button from '@components/Button';
import MemoizedIcon from '@components/MemoizedIcon';
import Modal from '@components/Modal';
import pathnames from '@src/app/routes/pathnames';
import QueryParamKeys from '@src/constants/queryParams';
import { useAuthorizationContext } from '@src/context/AuthorizationContext';

import { useTermsUpdateNotification } from './context';

interface TermsUpdateNotificationProps {
  onNext: () => void;
}

const TermsUpdateNotification: React.FC<TermsUpdateNotificationProps> = ({
  onNext,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { marketing_id } = useAuthorizationContext();

  const {
    checkHasNewTermsMap,
    handleMarkAsReadAll,
    hasNewTerms,
    handleSetNewTerms,
  } = useTermsUpdateNotification();
  const hasNewUnreadTerms = Object.values(checkHasNewTermsMap()).some(Boolean);

  const handleSeeNow = () => {
    // note: later will need to mark as read on the respective tab of terms type (the ui isn't available yet)
    handleMarkAsReadAll();

    handleSetNewTerms(false);

    history.push({
      pathname: pathnames.userGuide(),
      search: new URLSearchParams({
        [QueryParamKeys.Type]: 'termsUpdate',
      }).toString(),
    });
    onNext();
  };

  useEffect(() => {
    if (!marketing_id) return;

    if (hasNewUnreadTerms) {
      handleSetNewTerms(true);
    } else {
      handleSetNewTerms(false);
      onNext();
    }
  }, [handleSetNewTerms, hasNewUnreadTerms, marketing_id, onNext]);

  return (
    <Modal
      open={hasNewTerms}
      contentClassName="w-[536px] max-w-[calc(100vw-40px)] gap-4 max-h-[90vh] items-center p-4 sm:p-5"
      dataTestId="notification-modal"
    >
      <div className="flex justify-center items-center bg-primary-50 h-20 w-20 rounded-full">
        <MemoizedIcon
          icon={informationCircle}
          className="h-12 w-12 text-primary-500"
        />
      </div>
      <p className="text-center text-xl sm:text-2xl font-semibold">
        {t('userGuide.termsUpdate.notification.main')}
      </p>
      <p className="text-center text-base sm:text-lg">
        {t('userGuide.termsUpdate.notification.sub')}
      </p>
      <Button
        onClick={handleSeeNow}
        className="w-40 text-sm py-3"
        dataTestId="see-now-button"
      >
        {t('commons.seeNow')}
      </Button>
    </Modal>
  );
};

export default TermsUpdateNotification;
